import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  Fab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Close as CloseIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { FURNITURE_CATEGORIES } from '../../utils/categories';
import { normalizeCategory } from '../../utils/categorize';

const steps = ['Basic Info', 'Pricing & Stock', 'Images', 'Review'];

const AddItemModal = ({ 
  open, 
  onClose, 
  onSubmit, 
  isSubmitting = false 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Form state
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    description: '',
    category: { main: '', sub: '' },
    pricing: { cost: '', retail: '' },
    stock: { quantity: '', reorderPoint: '', maxQuantity: '' },
    supplier: { name: '', contact: '' },
    status: 'active'
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [cameraOpen, setCameraOpen] = useState(false);
  
  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setFormData({
        name: '',
        sku: '',
        description: '',
        category: { main: '', sub: '' },
        pricing: { cost: '', retail: '' },
        stock: { quantity: '', reorderPoint: '', maxQuantity: '' },
        supplier: { name: '', contact: '' },
        status: 'active'
      });
      setSelectedImages([]);
      setCameraOpen(false);
    }
  }, [open]);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (videoRef.current && videoRef.current.srcObject) {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach(track => track.stop());
      }
      // Cleanup image URLs
      selectedImages.forEach(image => {
        if (image.url && image.url.startsWith('blob:')) {
          URL.revokeObjectURL(image.url);
        }
      });
    };
  }, [selectedImages]);

  const handleClose = () => {
    // Stop camera if running
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setCameraOpen(false);
    onClose();
  };

  const handleFormChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  // Camera Functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setCameraOpen(true);
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Unable to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setCameraOpen(false);
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to blob
    canvas.toBlob((blob) => {
      if (blob) {
        const imageUrl = URL.createObjectURL(blob);
        const newImage = {
          file: new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' }),
          url: imageUrl
        };
        setSelectedImages(prev => [...prev, newImage]);
        stopCamera();
      }
    }, 'image/jpeg', 0.8);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const newImages = files.map(file => ({
      file,
      url: URL.createObjectURL(file)
    }));
    setSelectedImages(prev => [...prev, ...newImages]);
    event.target.value = '';
  };

  const removeImage = (index) => {
    const imageToRemove = selectedImages[index];
    if (imageToRemove.url && imageToRemove.url.startsWith('blob:')) {
      URL.revokeObjectURL(imageToRemove.url);
    }
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const submitData = new FormData();
    
    // Add form data
    Object.keys(formData).forEach(key => {
      if (typeof formData[key] === 'object' && formData[key] !== null) {
        Object.keys(formData[key]).forEach(subKey => {
          if (formData[key][subKey]) {
            submitData.append(`${key}.${subKey}`, formData[key][subKey]);
          }
        });
      } else if (formData[key]) {
        submitData.append(key, formData[key]);
      }
    });

    // Add images
    selectedImages.forEach((image, index) => {
      submitData.append('images', image.file);
    });

    await onSubmit(submitData);
  };

  const isStepValid = (step) => {
    switch (step) {
      case 0:
        return formData.name && formData.sku && formData.category.main;
      case 1:
        return formData.pricing.retail && formData.stock.quantity;
      case 2:
        return true; // Images are optional
      case 3:
        return true; // Review step
      default:
        return false;
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Product Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SKU"
                value={formData.sku}
                onChange={(e) => handleFormChange('sku', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Main Category</InputLabel>
                <Select
                  value={formData.category.main}
                  onChange={(e) => {
                    const normalized = normalizeCategory(e.target.value);
                    handleFormChange('category.main', normalized);
                    handleFormChange('category.sub', ''); // Reset subcategory
                  }}
                >
                  {FURNITURE_CATEGORIES.map(cat => (
                    <MenuItem key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Sub Category</InputLabel>
                <Select
                  value={formData.category.sub}
                  onChange={(e) => handleFormChange('category.sub', e.target.value)}
                  disabled
                >
                  <MenuItem value="">No subcategories</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cost Price"
                type="number"
                value={formData.pricing.cost}
                onChange={(e) => handleFormChange('pricing.cost', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Retail Price"
                type="number"
                value={formData.pricing.retail}
                onChange={(e) => handleFormChange('pricing.retail', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Initial Quantity"
                type="number"
                value={formData.stock.quantity}
                onChange={(e) => handleFormChange('stock.quantity', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Reorder Point"
                type="number"
                value={formData.stock.reorderPoint}
                onChange={(e) => handleFormChange('stock.reorderPoint', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Max Quantity"
                type="number"
                value={formData.stock.maxQuantity}
                onChange={(e) => handleFormChange('stock.maxQuantity', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Supplier Name"
                value={formData.supplier.name}
                onChange={(e) => handleFormChange('supplier.name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Supplier Contact"
                value={formData.supplier.contact}
                onChange={(e) => handleFormChange('supplier.contact', e.target.value)}
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Product Images
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add images to help identify your product (optional)
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<CameraIcon />}
                onClick={startCamera}
                disabled={cameraOpen}
              >
                Take Photo
              </Button>
              <Button
                variant="outlined"
                startIcon={<PhotoLibraryIcon />}
                onClick={() => fileInputRef.current?.click()}
              >
                Upload Images
              </Button>
            </Box>

            {/* Camera View */}
            {cameraOpen && (
              <Box sx={{ position: 'relative', mb: 2 }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  style={{
                    width: '100%',
                    maxHeight: '300px',
                    borderRadius: '8px'
                  }}
                />
                <Box sx={{ 
                  position: 'absolute', 
                  bottom: 16, 
                  left: '50%', 
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 1
                }}>
                  <Fab
                    color="primary"
                    onClick={capturePhoto}
                  >
                    <CameraIcon />
                  </Fab>
                  <Fab
                    color="secondary"
                    onClick={stopCamera}
                  >
                    <CloseIcon />
                  </Fab>
                </Box>
              </Box>
            )}

            {/* Image Preview */}
            {selectedImages.length > 0 && (
              <ImageList cols={isMobile ? 2 : 4} gap={8}>
                {selectedImages.map((image, index) => (
                  <ImageListItem key={index}>
                    <img
                      src={image.url}
                      alt={`Product ${index + 1}`}
                      style={{ height: '120px', objectFit: 'cover' }}
                    />
                    <ImageListItemBar
                      actionIcon={
                        <IconButton
                          sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                          onClick={() => removeImage(index)}
                        >
                          <CloseIcon />
                        </IconButton>
                      }
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              multiple
              accept="image/*"
              style={{ display: 'none' }}
            />
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Item Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Name:</Typography>
                <Typography variant="body1" gutterBottom>{formData.name}</Typography>
                
                <Typography variant="subtitle2">SKU:</Typography>
                <Typography variant="body1" gutterBottom>{formData.sku}</Typography>
                
                <Typography variant="subtitle2">Category:</Typography>
                <Typography variant="body1" gutterBottom>
                  {formData.category.main}{formData.category.sub ? ` / ${formData.category.sub}` : ''}
                </Typography>
                
                <Typography variant="subtitle2">Description:</Typography>
                <Typography variant="body1" gutterBottom>{formData.description || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Pricing:</Typography>
                <Typography variant="body1" gutterBottom>
                  Cost: ${formData.pricing.cost || '0'} | Retail: ${formData.pricing.retail || '0'}
                </Typography>
                
                <Typography variant="subtitle2">Stock:</Typography>
                <Typography variant="body1" gutterBottom>
                  Quantity: {formData.stock.quantity} | Reorder: {formData.stock.reorderPoint || 'N/A'} | Max: {formData.stock.maxQuantity || 'N/A'}
                </Typography>
                
                <Typography variant="subtitle2">Supplier:</Typography>
                <Typography variant="body1" gutterBottom>
                  {formData.supplier.name || 'N/A'}
                </Typography>
                
                <Typography variant="subtitle2">Status:</Typography>
                <Typography variant="body1" gutterBottom>
                  {formData.status.replace('_', ' ').toUpperCase()}
                </Typography>
              </Grid>
              {selectedImages.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Images ({selectedImages.length}):</Typography>
                  <ImageList cols={isMobile ? 3 : 6} gap={4} sx={{ mt: 1 }}>
                    {selectedImages.map((image, index) => (
                      <ImageListItem key={index}>
                        <img
                          src={image.url}
                          alt={`Product ${index + 1}`}
                          style={{ height: '80px', objectFit: 'cover' }}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Grid>
              )}
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Add New Item</DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {renderStepContent(activeStep)}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
          <Button onClick={handleClose} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            Cancel
          </Button>
          {activeStep > 0 && (
            <Button onClick={handleBack} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              Back
            </Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!isStepValid(activeStep)}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!isStepValid(activeStep) || isSubmitting}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {isSubmitting ? 'Adding Item...' : 'Add Item'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Hidden canvas for photo capture */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </>
  );
};

export default AddItemModal;
